import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { EN_US_TEXT } from "../i18n/en-US";
import { ENGLISH_LOCALE, getLocaleFromPathname } from "../lib/locale";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE"]);
const TRANSLATED_ATTRS = ["aria-label", "title", "alt", "placeholder"];
const NO_TRANSLATE_SELECTOR = "[data-no-translate]";

const ENCODING_REPAIRS = [
    [/\u00c2\u00b7/g, "·"],
    [/\u00e2\u0080\u0094/g, "—"],
    [/\u00c3\u0081/g, "Á"],
    [/\u00c3\u0089/g, "É"],
    [/\u00c3\u008d/g, "Í"],
    [/\u00c3\u0093/g, "Ó"],
    [/\u00c3\u009a/g, "Ú"],
    [/\u00c3\u0087/g, "Ç"],
    [/\u00c3\u00a1/g, "á"],
    [/\u00c3\u00a0/g, "à"],
    [/\u00c3\u00a2/g, "â"],
    [/\u00c3\u00a3/g, "ã"],
    [/\u00c3\u00a7/g, "ç"],
    [/\u00c3\u00a9/g, "é"],
    [/\u00c3\u00aa/g, "ê"],
    [/\u00c3\u00ad/g, "í"],
    [/\u00c3\u00b3/g, "ó"],
    [/\u00c3\u00b4/g, "ô"],
    [/\u00c3\u00b5/g, "õ"],
    [/\u00c3\u00ba/g, "ú"],
];

const repairEncoding = (value) =>
    ENCODING_REPAIRS.reduce((text, [pattern, replacement]) => text.replace(pattern, replacement), value ?? "");

const normalizeText = (value) => repairEncoding(value).replace(/\s+/g, " ").trim();

const TRANSLATION_LOOKUP = new Map(Object.entries(EN_US_TEXT).map(([key, value]) => [normalizeText(key), value]));
const ORIGINAL_TEXT = new WeakMap();
const ORIGINAL_ATTRS = new WeakMap();

const replaceTextNode = (node) => {
    const source = ORIGINAL_TEXT.get(node) ?? node.nodeValue;
    const key = normalizeText(source);
    const translation = TRANSLATION_LOOKUP.get(key);
    if (!key || !translation) return;

    if (!ORIGINAL_TEXT.has(node)) ORIGINAL_TEXT.set(node, source);

    const leading = source.match(/^\s*/)?.[0] || "";
    const trailing = source.match(/\s*$/)?.[0] || "";
    const nextValue = `${leading}${translation}${trailing}`;
    if (node.nodeValue !== nextValue) node.nodeValue = nextValue;
};

const restoreTextNode = (node) => {
    const original = ORIGINAL_TEXT.get(node);
    if (typeof original === "string" && node.nodeValue !== original) {
        node.nodeValue = original;
    }
};

const getTranslatedAttrNodes = (root) => {
    const selector = TRANSLATED_ATTRS.map((attr) => `[${attr}]`).join(",");
    const nodes = [...(root.querySelectorAll?.(selector) || [])];
    if (root.matches?.(selector)) nodes.unshift(root);
    return nodes.filter((node) => !node.closest?.(NO_TRANSLATE_SELECTOR));
};

const rememberOriginalAttr = (node, attr, value) => {
    let attrs = ORIGINAL_ATTRS.get(node);
    if (!attrs) {
        attrs = new Map();
        ORIGINAL_ATTRS.set(node, attrs);
    }
    if (!attrs.has(attr)) attrs.set(attr, value);
};

const translateAttributes = (root) => {
    const nodes = getTranslatedAttrNodes(root);
    nodes.forEach((node) => {
        TRANSLATED_ATTRS.forEach((attr) => {
            const value = node.getAttribute(attr);
            const source = ORIGINAL_ATTRS.get(node)?.get(attr) ?? value;
            const key = normalizeText(source);
            const translation = TRANSLATION_LOOKUP.get(key);
            if (key && translation) {
                rememberOriginalAttr(node, attr, source);
                if (value !== translation) node.setAttribute(attr, translation);
            }
        });
    });
};

const restoreAttributes = (root) => {
    const nodes = getTranslatedAttrNodes(root);
    nodes.forEach((node) => {
        const attrs = ORIGINAL_ATTRS.get(node);
        if (!attrs) return;
        attrs.forEach((value, attr) => {
            if (node.getAttribute(attr) !== value) node.setAttribute(attr, value);
        });
    });
};

const walkTextNodes = (root, callback) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            const parent = node.parentElement;
            if (!parent || SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
            if (parent.closest?.(NO_TRANSLATE_SELECTOR)) return NodeFilter.FILTER_REJECT;
            if (!normalizeText(node.nodeValue)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
        },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(callback);
};

const translateTree = (root) => {
    translateAttributes(root);
    walkTextNodes(root, replaceTextNode);
};

const restoreTree = (root) => {
    restoreAttributes(root);
    walkTextNodes(root, restoreTextNode);
};

const LocaleTextLayer = () => {
    const { pathname } = useLocation();
    const locale = getLocaleFromPathname(pathname);

    useEffect(() => {
        if (locale !== ENGLISH_LOCALE) {
            restoreTree(document.body);
            return undefined;
        }

        const timers = [];
        const run = () => translateTree(document.body);
        let frame = window.requestAnimationFrame(run);
        [60, 160, 360, 760, 1400].forEach((delay) => {
            timers.push(window.setTimeout(run, delay));
        });

        const observer = new MutationObserver(() => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(run);
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: TRANSLATED_ATTRS,
        });

        return () => {
            observer.disconnect();
            window.cancelAnimationFrame(frame);
            timers.forEach((timer) => window.clearTimeout(timer));
            restoreTree(document.body);
        };
    }, [locale, pathname]);

    return null;
};

export default LocaleTextLayer;
